import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'components/Map/utils';
import { Layer, LayerProps } from 'shared/map-exports';
import addVisible from 'shared/addVisible';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { useMapLayerEvents } from '../useMapLayerEvents';
import {
  ExternalFeatureLayerProps,
  ExternalFeatureProperties,
  ExternalLayerStylesInput,
} from './ExternalFeatureLayer.types';
import { useLayerClickHandler } from './useLayerClickHandler';

type PolygonLayersProps = ExternalFeatureLayerProps;

const getPolygonFillLayerStyles = (
  input: ExternalLayerStylesInput,
): LayerProps => {
  const { sourceId, sourceLayer, minZoom, maxZoom, color } = input;
  return {
    type: 'fill',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: minZoom,
    maxzoom: maxZoom,
    paint: {
      'fill-color': color,
      'fill-opacity': 0.25,
    },
  };
};

const getPolygonStrokeLayerStyles = (
  input: ExternalLayerStylesInput,
): LayerProps => {
  const { sourceId, sourceLayer, minZoom, maxZoom, color } = input;
  return {
    type: 'line',
    source: sourceId,
    'source-layer': sourceLayer,
    minzoom: minZoom,
    maxzoom: maxZoom,
    paint: {
      'line-color': color,
      'line-opacity': 0.75,
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
    },
  };
};

export const PolygonLayers = (props: PolygonLayersProps): JSX.Element => {
  const {
    visible,
    sourceId,
    featureLayer: { shortName, displayStyleData },
  } = props;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const clickHandler = useLayerClickHandler(sourceId);

  useMapLayerEvents<ExternalFeatureProperties>({
    layerId: `${sourceId}_fill`,
    onClick: clickHandler,
  });

  const selectedId =
    (mapDrawerEntityForPoi?.type === sourceId && mapDrawerEntityForPoi?.id) ||
    '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
      minzoom: displayStyleData.minZoom,
      maxzoom: displayStyleData.maxZoom,
    },
    selectedId,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
      minzoom: displayStyleData.minZoom,
      maxzoom: displayStyleData.maxZoom,
      paint: {
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
      },
    },
    selectedId,
  });

  return (
    <>
      <Layer
        id={`${sourceId}_fill`}
        {...addVisible(
          getPolygonFillLayerStyles({
            sourceId,
            sourceLayer: shortName,
            ...displayStyleData,
          }),
          visible,
        )}
      />

      <Layer
        id={`${sourceId}_stroke`}
        {...addVisible(
          getPolygonStrokeLayerStyles({
            sourceId,
            sourceLayer: shortName,
            ...displayStyleData,
          }),
          visible,
        )}
      />

      <Layer {...selectedFillStyle} />
      <Layer {...selectedStrokeStyle} />
    </>
  );
};
